/**
 * Cliente do gateway. Ver docs/03-gateway-protocol.md.
 *
 * A reconexão é o caminho mais percorrido de um cliente real — Wi-Fi a cair,
 * portátil a suspender, túnel 4G. Tenta sempre RESUME primeiro: custa um
 * LRANGE no servidor, contra uma query de guilds e um READY completo.
 */
import {
  CloseCode,
  Op,
  reconnectDelay,
  shouldReconnect,
  type Frame,
} from '@tasca/shared';
import { getAccessToken } from './rest.js';
import { sdk, wsOrigin } from './config.js';

export type GatewayStatus = 'idle' | 'connecting' | 'ready' | 'reconnecting' | 'dead';

export interface GatewayHandlers {
  onEvent: (event: string, data: unknown) => void;
  onStatus: (status: GatewayStatus) => void;
  /** Token inválido ou sessão expirada: o cliente deve ir para o login. */
  onFatal: () => void;
}

export class GatewayClient {
  #socket: WebSocket | null = null;
  #heartbeat: ReturnType<typeof setInterval> | null = null;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #attempt = 0;
  #seq = 0;
  #sessionId: string | null = null;
  #intentionalClose = false;
  #status: GatewayStatus = 'idle';

  constructor(private readonly handlers: GatewayHandlers) {}

  get status(): GatewayStatus {
    return this.#status;
  }

  #setStatus(s: GatewayStatus): void {
    if (this.#status === s) return;
    this.#status = s;
    this.handlers.onStatus(s);
  }

  connect(): void {
    if (this.#socket && this.#socket.readyState <= 1) return;

    this.#intentionalClose = false;
    this.#setStatus(this.#sessionId ? 'reconnecting' : 'connecting');

    // A origem vem da configuracao e nao de `location`: numa app instalada a
    // pagina vem do bundle local e `location.host` seria o protocolo de
    // assets do webview. A fabrica de socket tambem e injectada, para quem
    // nao tenha um global utilizavel.
    const socket = sdk().websocket(`${wsOrigin()}/ws?v=1&encoding=json`);
    this.#socket = socket;

    socket.onmessage = (ev) => this.#onFrame(ev);

    socket.onclose = (ev) => {
      this.#stopHeartbeat();
      if (this.#intentionalClose) {
        this.#setStatus('idle');
        return;
      }

      // 4004/4009 e companhia: reconectar em loop com um token morto é como
      // se derruba o próprio servidor.
      if (!shouldReconnect(ev.code)) {
        this.#sessionId = null;
        this.#seq = 0;
        this.#setStatus('dead');
        this.handlers.onFatal();
        return;
      }

      this.#scheduleReconnect();
    };

    socket.onerror = () => {
      /* o onclose que segue trata da reconexão */
    };
  }

  disconnect(): void {
    this.#intentionalClose = true;
    this.#stopHeartbeat();
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);
    this.#sessionId = null;
    this.#seq = 0;
    this.#socket?.close(1000, 'client disconnect');
    this.#socket = null;
    this.#setStatus('idle');
  }

  send(op: number, d?: unknown): void {
    if (this.#socket?.readyState !== 1) return;
    this.#socket.send(JSON.stringify(d === undefined ? { op } : { op, d }));
  }

  subscribe(guildId: string, channelIds: string[]): void {
    this.send(Op.SUBSCRIBE, { guildId, channelIds });
  }

  requestMembers(guildId: string, limit = 100): void {
    this.send(Op.REQUEST_MEMBERS, { guildId, limit });
  }

  /** Só mudanças de estado, nunca por frame. Ver docs/05 e voice/client.ts. */
  speaking(speaking: boolean): void {
    this.send(Op.SPEAKING_UPDATE, { speaking });
  }

  updatePresence(status: 'online' | 'idle' | 'dnd' | 'invisible'): void {
    this.send(Op.PRESENCE_UPDATE, { status });
  }

  #onFrame(ev: MessageEvent<string>): void {
    let frame: Frame;
    try {
      frame = JSON.parse(ev.data) as Frame;
    } catch {
      return;
    }

    switch (frame.op) {
      case Op.HELLO: {
        const d = frame.d as { heartbeatIntervalMs: number };
        this.#startHeartbeat(d.heartbeatIntervalMs);
        this.#identifyOrResume();
        return;
      }

      case Op.HEARTBEAT_ACK:
        return;

      case Op.DISPATCH: {
        if (typeof frame.s === 'number') this.#seq = frame.s;
        const event = frame.t ?? '';

        if (event === 'READY') {
          const d = frame.d as { sessionId: string };
          this.#sessionId = d.sessionId;
          this.#attempt = 0;
          this.#setStatus('ready');
        } else if (event === 'RESUMED') {
          this.#attempt = 0;
          this.#setStatus('ready');
        }

        this.handlers.onEvent(event, frame.d);
        return;
      }

      case Op.INVALID_SESSION: {
        const d = frame.d as { resumable: boolean } | undefined;
        if (!d?.resumable) {
          // Recomeçar de zero: o buffer do servidor já rodou por cima do
          // que nos falta.
          this.#sessionId = null;
          this.#seq = 0;
        }
        return;
      }

      case Op.RECONNECT: {
        this.#socket?.close(4000, 'server asked to reconnect');
        return;
      }

      default:
        return;
    }
  }

  #identifyOrResume(): void {
    const token = getAccessToken();
    if (!token) {
      this.#setStatus('dead');
      this.handlers.onFatal();
      return;
    }

    if (this.#sessionId && this.#seq > 0) {
      this.send(Op.RESUME, { token, sessionId: this.#sessionId, s: this.#seq });
      return;
    }

    this.send(Op.IDENTIFY, {
      token,
      capabilities: 0,
      properties: {
        os: navigator.platform || 'web',
        client: 'tasca-web',
        version: '0.1.0',
      },
    });
  }

  #startHeartbeat(intervalMs: number): void {
    this.#stopHeartbeat();
    // Jitter no PRIMEIRO intervalo: sem isto, todos os clientes batem no
    // mesmo segundo depois de um reinício do servidor.
    const first = Math.floor(intervalMs * Math.random());
    setTimeout(() => {
      this.send(Op.HEARTBEAT, { s: this.#seq || null });
      this.#heartbeat = setInterval(() => {
        this.send(Op.HEARTBEAT, { s: this.#seq || null });
      }, intervalMs);
    }, first);
  }

  #stopHeartbeat(): void {
    if (this.#heartbeat) {
      clearInterval(this.#heartbeat);
      this.#heartbeat = null;
    }
  }

  #scheduleReconnect(): void {
    this.#attempt += 1;
    const delay = reconnectDelay(this.#attempt);
    this.#setStatus('reconnecting');
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = setTimeout(() => this.connect(), delay);
  }
}

export { CloseCode };
